import React from 'react';
import ContractStatus from '../status/ContractStatus';
import ConceptSelector from '../selectors/ConceptSelector';
import ChainSelector from '../selectors/ChainSelector';
import WalletDisplay from '../common/WalletDisplay';
import MintControls from './MintControls';
import ResultsModal from '../modals/ResultsModal';
import SocialLinks from '../layout/SocialLinks';
import TermsModal from '../modals/TermsModal';
import Receipt from '../receipt/Receipt';
import { CHAIN_OPTIONS } from '../../constants/networks';
import { SafeMath } from '../../utils/SafeMath';

const MintingInterface = ({
    state,
    updateState,
    handleMint,
    handleClaim,
    getButtonState,
    modal,
    showTerms,
    setShowTerms,
    handleTermsAccept
}) => {
    const mintButtonState = getButtonState('mint');
    const claimButtonState = getButtonState('claim');

    const handleConceptSelect = (id) => {
        updateState({ selectedConcept: id });
    };

    const handleChainChange = (e) => {
        const chain = e.target.value;
        const selectedChain = CHAIN_OPTIONS.find((c) => c.value === chain);
        updateState({
            selectedChain: selectedChain.value,
            selectedChainNo: selectedChain.chainNo
        });
    };

    const handleMintAmountChange = (e) => {
        updateState({ mintAmount: parseInt(e.target.value) });
    };

    const totalPrice = React.useMemo(() => {
        try {
            const priceInWei = SafeMath.parseEther(state.price);
            const totalPriceInWei = SafeMath.multiply(priceInWei, state.mintAmount);
            return SafeMath.formatEther(totalPriceInWei);
        } catch (error) {
            console.error('Price calculation error:', error);
            return "0";
        }
    }, [state.price, state.mintAmount]);

    return (
        <div className="max-w-4xl w-full mx-auto p-4 bg-background rounded-lg shadow-lg scale-90 origin-center">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div>
                    <h1 className="text-3xl font-semibold text-primary heading mb-2">
                        Dæmons – Gamefi x AI
                    </h1>
                    {state.isConnected && <WalletDisplay address={state.address} />}
                    <ContractStatus
                        whitelistMint={state.whitelistMint}
                        publicMint={state.publicMint}
                        isWhitelisted={state.isWhitelisted}
                    />
                    <p className="text-primary mb-2 custom-font">
                        Join the Dæmons journey!
                        <br />
                        Each mint casts your vote, shaping the art style and launch chain of the Dæmons game. You'll also receive free Dæmon pets when the game launches, along with a boost to your Dæmon Soul Points!
                    </p>

                    <p className="text-primary font-bold mb-2 custom-font">
                        Note: There is a 5 NFT per wallet limit. Full Mint FAQ in Discord.
                    </p>

                    <div className="space-y-2 mb-3">
                        <ConceptSelector
                            selectedConcept={state.selectedConcept}
                            onConceptSelect={handleConceptSelect}
                        />

                        <ChainSelector
                            selectedChain={state.selectedChain}
                            onChange={handleChainChange}
                        />

                        {state.publicMint && state.isConnected && (
                            <MintControls
                                mintAmount={state.mintAmount}
                                onChange={handleMintAmountChange}
                                price={state.price}
                                totalPrice={totalPrice}
                                selectedConcept={state.selectedConcept}
                            />
                        )}

                        <p className="text-sm text-primary mb-2 custom-font">
                            Remaining: {state.maxSupply - state.currentSupply}/{state.maxSupply}
                        </p>
                    </div>

                    <div className="flex flex-col lg:flex-row items-center lg:space-x-3 space-y-2 lg:space-y-0">
                        {!state.isConnected ? (
                            <button
                                className="btn btn-primary"
                                onClick={() => modal.open()}
                            >
                                Connect Wallet
                            </button>
                        ) : (
                            <>
                                {state.whitelistMint && state.isWhitelisted && (
                                    <button
                                        className={`btn ${claimButtonState.isDisabled ? 'btn-disabled' : 'btn-primary'}`}
                                        onClick={handleClaim}
                                        disabled={claimButtonState.isDisabled}
                                    >
                                        {claimButtonState.text}
                                    </button>
                                )}

                                {state.publicMint && (
                                    <button
                                        className={`btn ${mintButtonState.isDisabled ? 'btn-disabled' : 'btn-primary'}`}
                                        onClick={handleMint}
                                        disabled={mintButtonState.isDisabled}
                                    >
                                        {mintButtonState.text}
                                    </button>
                                )}

                                <button
                                    onClick={() => updateState({ isModalOpen: true })}
                                    className="btn btn-primary"
                                >
                                    View Results
                                </button>
                            </>
                        )}
                    </div>
                    <div className="mt-3">
                        <SocialLinks />
                    </div>
                </div>

                <div className="flex justify-center items-center">
                    <Receipt state={state} totalPrice={totalPrice} />
                </div>
            </div>

            <TermsModal
                isOpen={showTerms}
                onAccept={handleTermsAccept}
                onClose={() => setShowTerms(false)}
            />

            <ResultsModal
                isOpen={state.isModalOpen}
                onClose={() => updateState({ isModalOpen: false })}
                votesConcept={state.votesConcept}
                chainConcept={state.chainConcept}
            />
        </div>
    );
};

export default MintingInterface;